/**
 * Environment variable validation and configuration
 * 
 * This module validates required environment variables at startup and fails fast
 * if they are missing. This prevents runtime errors from misconfiguration.
 * 
 * Constitution Section 19.3: Environment Variables & Configuration
 */

/**
 * Validates and returns the API base URL from environment variables.
 * Throws an error if not set (fail-fast pattern).
 */
export function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_API_BASE_URL;
  
  if (!url || url.trim() === '') {
    throw new Error(
      'VITE_API_BASE_URL is required but not set. ' +
      'Please set it in your .env file or environment variables.'
    );
  }
  
  // Remove trailing slashes
  return url.trim().replace(/\/+$/, '');
}

/**
 * Validates and returns the chat service URL from environment variables.
 * Throws an error if not set (fail-fast pattern).
 */
export function getChatServiceUrl(): string {
  const url = import.meta.env.VITE_CHAT_SERVICE_URL;
  
  if (!url || url.trim() === '') {
    throw new Error(
      'VITE_CHAT_SERVICE_URL is required but not set. ' +
      'Please set it in your .env file or environment variables.'
    );
  }
  
  // Remove trailing slashes
  return url.trim().replace(/\/+$/, '');
}

/**
 * Get API base URL (validated)
 */
export const API_BASE_URL = getApiBaseUrl();

/**
 * Get chat service URL (validated)
 */
export const CHAT_SERVICE_URL = getChatServiceUrl();

/**
 * Returns the wizard service URL from environment variables.
 * Falls back to localhost:8003 for local development.
 */
export function getWizardServiceUrl(): string {
  const url = import.meta.env.VITE_WIZARD_SERVICE_URL;

  if (!url || url.trim() === '') {
    if (import.meta.env.PROD) {
      console.warn('[env] VITE_WIZARD_SERVICE_URL not set in production, using localhost fallback');
    }
    return 'http://localhost:8003';
  }

  // Remove trailing slashes
  return url.trim().replace(/\/+$/, '');
}

/**
 * Get wizard service URL (with fallback)
 */
export const WIZARD_SERVICE_URL = getWizardServiceUrl();
